import { Controller } from "@hotwired/stimulus"
import ProgressBar from "../helpers/progress_bar"

const JQUERY_NAMESPACE = ".giftEvents"

// Paired with the shared `datatable` controller on the same wrapping element: that one owns the
// DataTables lifecycle and the `?id=` row highlight, this one owns the date-range picker, the four
// select2 filters and the "All stages" special case below.
export default class extends Controller {
  static get targets() {
    return ["table", "dateRangeInput", "giftStatusFilter", "senderFilter", "ccFilter", "campaignFilter"];
  }

  static get values() {
    return { begin: String, end: String };
  }

  static get classes() {
    return ["open"];
  }

  connect() {
    $(this.dateRangeInputTarget).daterangepicker({
      buttonClasses: "btn btn-main btn-md",
      cancelButtonClasses: "btn-ghost",
      opens: "left",
      alwaysShowCalendars: true,
      showCustomRangeLabel: false,
      ranges: {
        "Today": [moment(), moment()],
        "Yesterday": [moment().subtract(1, "days"), moment().subtract(1, "days")],
        "Last 7 Days": [moment().subtract(6, "days"), moment()],
        "Last 30 Days": [moment().subtract(29, "days"), moment()],
        "This Month": [moment().startOf("month"), moment().endOf("month")],
        "Last Month": [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")],
        "All Time": [moment(this.beginValue, "MM-DD-YYYY"), moment(this.endValue, "MM-DD-YYYY")],
      },
    }, (start, end) => this.applyRange(start, end));

    // show.daterangepicker/hide.daterangepicker are announced by the plugin through jQuery's
    // trigger, not a real DOM event, so the binding itself has to stay on jQuery -- but the class
    // toggle it runs is plain DOM work on our own static markup.
    $(this.dateRangeInputTarget).
      on(`show.daterangepicker${JQUERY_NAMESPACE}`, () => this.element.querySelector(".date-range-picker").classList.add(this.openClass)).
      on(`hide.daterangepicker${JQUERY_NAMESPACE}`, () => this.element.querySelector(".date-range-picker").classList.remove(this.openClass));

    $(this.giftStatusFilterTarget).select2({ placeholder: "Gift Status", width: "100%" });
    $(this.senderFilterTarget).select2({ placeholder: "Created By", width: "100%" });

    if (this.hasCcFilterTarget) {
      $(this.ccFilterTarget).select2({ placeholder: "Sent From", width: "100%", minimumResultsForSearch: Infinity });
    }
    if (this.hasCampaignFilterTarget) {
      $(this.campaignFilterTarget).select2({ placeholder: "Campaign", width: "100%", minimumResultsForSearch: Infinity });
    }

    // "All stages" (value "all") is not a real stage: selecting it force-selects every other
    // option in the same select, unselecting it clears them, and either way it re-triggers
    // "change" so the reload picks up the full set. select2 announces picks through jQuery's event
    // bus, so this stays a jQuery binding.
    $(this.giftStatusFilterTarget).on(`select2:select${JQUERY_NAMESPACE}`, (e) => {
      if (e.params && e.params.data && e.params.data.id === "all") {
        $(this.giftStatusFilterTarget).find("option").prop("selected", true);
        $(this.giftStatusFilterTarget).trigger("change");
      }
    });

    $(this.giftStatusFilterTarget).on(`select2:unselect${JQUERY_NAMESPACE}`, (e) => {
      if (e.params && e.params.data && e.params.data.id === "all") {
        $(this.giftStatusFilterTarget).find("option").prop("selected", false);
        $(this.giftStatusFilterTarget).trigger("change");
      }
    });

    $(this.element).on(`change${JQUERY_NAMESPACE}`, ".datatable-moveable-filter", () => this.reload());
  }

  disconnect() {
    const picker = $(this.dateRangeInputTarget).data("daterangepicker");
    if (picker) { picker.remove(); }
    $(this.dateRangeInputTarget).off(JQUERY_NAMESPACE);

    $(this.element).off(JQUERY_NAMESPACE);
    $(this.giftStatusFilterTarget).off(JQUERY_NAMESPACE);

    if ($(this.giftStatusFilterTarget).data("select2")) {
      $(this.giftStatusFilterTarget).select2("destroy");
    }
    if ($(this.senderFilterTarget).data("select2")) {
      $(this.senderFilterTarget).select2("destroy");
    }
    if (this.hasCcFilterTarget && $(this.ccFilterTarget).data("select2")) {
      $(this.ccFilterTarget).select2("destroy");
    }
    if (this.hasCampaignFilterTarget && $(this.campaignFilterTarget).data("select2")) {
      $(this.campaignFilterTarget).select2("destroy");
    }
  }

  applyRange(start, end) {
    const newRange = `${start.format("MMDDYYYY")}-${end.format("MMDDYYYY")}`;
    this.reload({ range: newRange });
  }

  reload(extraParams = {}) {
    ProgressBar.start();

    const $filterSelects = $(this.element).find(".datatable-moveable-filter, .datatable-filter");
    if ($filterSelects.length > 0) {
      const filter = {};
      $filterSelects.each((_, el) => {
        const $select = $(el);
        const key = $select.attr("name").replace(/(^.*\[|\].*$)/g, "");
        filter[key] = ($select.select2("data") || []).map((option) => option.id);
      });
      extraParams["filter"] = filter;
    }

    $(this.element).find(".datatable-moveable-text-filter, .datatable-text-filter").each((_, el) => {
      const $text = $(el);
      extraParams["filter"][$text.attr("name").replace(/(^.*\[|\].*$)/g, "")] = $text.val();
    });

    const params = Object.assign(this.currentParams(), extraParams);

    const replacement = `${this.tableTarget.getAttribute("data-source").split("?")[0]}?${$.param(params)}`;
    $(this.tableTarget).attr("data-source", replacement);
    $(this.tableTarget).DataTable().ajax.url(replacement).load(() => ProgressBar.stop());
  }

  // `data-source` on the table target is the app-wide datatable convention (decisions.md D8), not
  // a Values API candidate -- 15+ other datatable-driving controllers and several specs read/assert
  // this exact attribute name directly.
  currentParams() {
    return this.urlParamsAsObject(this.tableTarget.getAttribute("data-source"));
  }

  // Converts a bracket-notation query string into the nested object `$.param()` re-serializes
  // back into the same form. A URLSearchParams rewrite would lose the nesting.
  urlParamsAsObject(dataSource) {
    const query = dataSource.substring(dataSource.indexOf("?") + 1);
    const regex = /([^&=]+)=?([^&]*)/g;
    const decode = (str) => decodeURIComponent(str.replace(/\+/g, " "));

    const params = {};
    let match = regex.exec(query);
    while (match) {
      const k = decode(match[1]);
      const v = decode(match[2]);

      if (k.substring(k.length - 2) === "[]") {
        const key = k.substring(0, k.length - 2);
        (params[key] || (params[key] = [])).push(v);
      } else {
        params[k] = v;
      }

      match = regex.exec(query);
    }

    const assignPath = (obj, keyPath, value) => {
      const lastKeyIndex = keyPath.length - 1;
      let target = obj;
      for (let i = 0; i < lastKeyIndex; i++) {
        const key = keyPath[i];
        if (!(key in target)) { target[key] = {}; }
        target = target[key];
      }
      target[keyPath[lastKeyIndex]] = value;
    };

    Object.keys(params).forEach((prop) => {
      const structure = prop.split("[");
      if (structure.length > 1) {
        const levels = structure.map((part) => part.replace(/[?[\]\\ ]/g, ""));
        assignPath(params, levels, params[prop]);
        delete params[prop];
      }
    });

    return params;
  }
}
